import { fn } from 'storybook/test';

import type { Meta, StoryFn } from '@storybook/react';
import type { FC, PropsWithChildren } from 'react';
import type { SelectListboxProps } from './SelectListbox';

import { SelectListbox } from './SelectListbox';
import FlagNl from './stories-images/nl.svg?react';
import FlagEn from './stories-images/gb.svg?react';
import FlagDe from './stories-images/de.svg?react';
import FlagFr from './stories-images/fr.svg?react';
import FlagIt from './stories-images/it.svg?react';
import FlagDk from './stories-images/dk.svg?react';
import FlagEs from './stories-images/es.svg?react';

const LANGUAGE_CODES = ['NL', 'EN'] as const;
type LanguageCode = (typeof LANGUAGE_CODES)[number];
interface SelectListboxStoryProps extends SelectListboxProps {
  SwitchLanguage?: LanguageCode;
}

const DATA = [
  {
    label: 'Standaard Nederlands (NL) - Dutch (NL)',
    value: 'NL',
  },
  {
    label: 'English',
    value: 'EN',
  },
  {
    label: 'German',
    value: 'DE',
  },
  {
    label: 'French',
    value: 'FR',
  },
  {
    label: 'Italian',
    value: 'IT',
  },
  {
    label: 'Danish',
    value: 'DK',
  },
  {
    label: 'Spanish',
    value: 'ES',
  },
];

const codeString = `
  <SelectListbox options={mockData} defaultValue={defaultValue} variant={variant}
    size={size} buttonLabelBold={buttonLabelBold} onChange={onChangeFunction}>
      {mockData.map(({label, value}, index) => ( <SelectListbox.SelectOption option="{{label, value}}"
      index={index} key={value}> {label} </SelectListbox.SelectOption> ))} </SelectListbox>
`;

// eslint-disable-next-line import/no-default-export
export default {
  title: 'Organisms/SelectListbox',
  component: SelectListbox,
  argTypes: {
    options: {
      table: {
        disable: true,
      },
    },
    defaultValue: {
      table: {
        disable: true,
      },
    },
    onChange: {
      table: {
        disable: true,
      },
    },
    disabled: {
      control: 'boolean',
      default: false,
    },
    buttonLabelBold: {
      control: 'boolean',
      default: false,
    },
    variant: {
      options: ['default', 'noborder', 'pill', 'pillLeft', 'pillRight', 'darkBorder', 'roundedLeft', 'roundedRight'],
      control: { type: 'select' },
    },
    optionPositionRight: {
      control: 'boolean',
      default: false,
    },
    buttonLabelProp: {
      table: {
        disable: true,
      },
    },
    children: {
      table: {
        disable: true,
      },
    },
    SelectOption: {
      table: {
        disable: true,
      },
    },
    SelectOptionBox: {
      table: {
        disable: true,
      },
    },
    SelectButton: {
      table: {
        disable: true,
      },
    },
    SelectContainer: {
      table: {
        disable: true,
      },
    },
    SwitchLanguage: {
      control: 'inline-radio',
      options: LANGUAGE_CODES,
      description: 'Switch between Dutch and English language options',
    },
  },
  parameters: {
    inspectComponent: SelectListbox,
    codeString: codeString,
  },
} as Meta<SelectListboxStoryProps>;

const defaultArgs: Partial<PropsWithChildren<SelectListboxProps>> = {
  options: DATA,
  defaultValue: 'EN',
  disabled: false,
  buttonLabelBold: false,
  maxOptionHeight: '20rem',
  optionPositionRight: false,
  mobileBreakpoint: '28rem',
  size: 'default',
  onChange: fn(),
};

const Template: StoryFn<PropsWithChildren<SelectListboxStoryProps>> = (args) => (
  <div style={{ width: '30rem', margin: '0rem auto 0 auto' }}>
    <SelectListbox {...args}>
      {DATA.map(({ label, value }, index) => (
        <SelectListbox.SelectOption option={{ label, value }} index={index} key={`${value}`}>
          {label}
        </SelectListbox.SelectOption>
      ))}
    </SelectListbox>
  </div>
);

export const SelectListboxExample = Template.bind({});
SelectListboxExample.args = {
  ...defaultArgs,
};
SelectListboxExample.argTypes = {
  SwitchLanguage: {
    table: {
      disable: true,
    },
  },
};

const ComponentOption: FC<{ label: string; value: string }> = ({ label, value }) => {
  const flags = {
    NL: <FlagNl style={{ width: '24px', height: '24px', marginRight: '10px' }} />,
    EN: <FlagEn style={{ width: '24px', height: '24px', marginRight: '10px' }} />,
    DE: <FlagDe style={{ width: '24px', height: '24px', marginRight: '10px' }} />,
    FR: <FlagFr style={{ width: '24px', height: '24px', marginRight: '10px' }} />,
    IT: <FlagIt style={{ width: '24px', height: '24px', marginRight: '10px' }} />,
    DK: <FlagDk style={{ width: '24px', height: '24px', marginRight: '10px' }} />,
    ES: <FlagEs style={{ width: '24px', height: '24px', marginRight: '10px' }} />,
  };

  return (
    <div style={{ display: 'flex' }}>
      {flags[value as keyof typeof flags]}
      {label}
    </div>
  );
};

const TemplateWithComponent: StoryFn<PropsWithChildren<SelectListboxStoryProps>> = (args) => (
  <div style={{ width: '10rem', margin: '0rem auto 0 auto' }}>
    <SelectListbox {...args}>
      {args.options.map(({ label, value }, index) => (
        <SelectListbox.SelectOption option={{ label, value }} index={index} key={`${value}`}>
          <ComponentOption label={label} value={value as string} />
        </SelectListbox.SelectOption>
      ))}
    </SelectListbox>
  </div>
);

export const WithComponentOption = TemplateWithComponent.bind({});
WithComponentOption.args = {
  ...defaultArgs,
};
WithComponentOption.argTypes = {
  SwitchLanguage: {
    table: {
      disable: true,
    },
  },
};

const NL: { label: string; value: string }[] = [
  {
    label: 'Stoel',
    value: 'chair',
  },
  {
    label: 'Tafel',
    value: 'table',
  },
  {
    label: 'Bank',
    value: 'sofa',
  },
  {
    label: 'Bed',
    value: 'bed',
  },
  {
    label: 'Kledingkast',
    value: 'wardrobe',
  },
];

const EN: { label: string; value: string }[] = [
  {
    label: 'Chair',
    value: 'chair',
  },
  {
    label: 'Table',
    value: 'table',
  },
  {
    label: 'Sofa',
    value: 'sofa',
  },
  {
    label: 'Bed',
    value: 'bed',
  },
  {
    label: 'Wardrobe',
    value: 'wardrobe',
  },
];

const languageArgs: Partial<PropsWithChildren<SelectListboxStoryProps>> = {
  defaultValue: 'chair',
  disabled: false,
  buttonLabelBold: false,
  maxOptionHeight: '20rem',
  optionPositionRight: false,
  mobileBreakpoint: '28rem',
  size: 'default',
  onChange: fn(),
};

const TemplateWithLanguageSwitch: StoryFn<PropsWithChildren<SelectListboxStoryProps>> = (args) => {
  const { options: _ignored, ...rest } = args;
  const lang = args.SwitchLanguage ?? 'EN';
  const languageOptions = lang === 'NL' ? NL : EN;

  return (
    <div style={{ width: '50rem', margin: '0rem auto 0 auto' }}>
      <SelectListbox {...rest} options={languageOptions}>
        {languageOptions.map(({ label, value }, index) => (
          <SelectListbox.SelectOption option={{ label, value }} index={index} key={`${value}`}>
            {label}
          </SelectListbox.SelectOption>
        ))}
      </SelectListbox>
    </div>
  );
};

export const WithLanguageSwitch = TemplateWithLanguageSwitch.bind({});
WithLanguageSwitch.args = {
  ...languageArgs,
};
